import { useEffect, useRef } from "react";
import type { ChangeEvent, FocusEvent, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import * as amplitude from "@amplitude/analytics-browser";

import {
  Button,
  Checkbox,
  getPaymentMethod,
  Input,
  isThousandOperand,
  ItemSelectorGroup,
  Modal,
  Tooltip,
} from "@unified-checkout/design-system";
import { CircleInfo } from "@unified-checkout/design-system/icons/index";

import {
  BULK_PAYMENT_LINKS_EVENTS,
  BULK_PAYMENT_LINKS_EVENTS_PARAMS
} from "@app/dashboard/constants/tracking_events/payment_links/bulk";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import type { IPaymentSection, TSelectedMethod } from "@app/dashboard/types/innerLayout/payment_links/bulk/sections/payment-section";
import { RemovableBadgesGroup } from "@app/dashboard/components/inner_layout/RemovableBadgesGroup";
import { PaymentMethodsTogglesGroup } from "@app/dashboard/components/inner_layout/PaymentMethodsTogglesGroup";
import { deleteItemsFromObj } from "@app/dashboard/utils/deleteItemsFromObj";
import { getCurrentMode } from "@app/dashboard/utils/getCurrentMode";
import {
  AMOUNTS_FROM,
  badgeTextFormatter,
  FIELDS_IDS,
  fixedAmountValidator,
  INPUT_DD_PROPS,
  remainingItemsBadgeTextFormatter,
  BULK_PLS_SECTIONS_DIMENSIONS,
  preventExponentialNotationInput,
} from "../../../utils";
import {
  usePaymentSectionLogic
} from "../../../hooks/usePaymentSectionLogic";
import { SectionLoader } from "../../../Loader";
import styles from "./payment-section.module.scss";


export const PaymentSection = ({
  amountsFrom,
  setAmountsFrom,
  fixedAmount,
  setFixedAmount,
  setFormValues,
  formErrors,
  setFormErrors,
  fullPageStatus,
  setFullPageStatus
}: IPaymentSection) => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();
  const queryClient = useQueryClient();

  const {
    isPreSelectedMethodsLoading,
    isPreSelectedMethodsError,

    currentCurrency,
    setCurrentCurrency,

    availableCurrencies,

    tempSelectedMethods,
    setTempSelectedMethods,

    savedMethods,
    setSavedMethods,

    isModalOpen,
    setIsModalOpen,

    modalActionBtns,
    isMethodsLoading,
    canChangeCurrency,
    orderedPaymentMethods,
    getFormattedCurrency,
    canViewPaymentIntegrations
  } = usePaymentSectionLogic({ setFullPageStatus });

  const fixedAmountValueRef = useRef<string>("");

  useEffect(() => {
    setFormErrors(prev => deleteItemsFromObj(prev, FIELDS_IDS.FIXED_AMOUNT));
  }, [amountsFrom]);

  useEffect(() => {
    const paymentMethodsIds = Object.values(savedMethods || {}).map(method => String(method.id));

    setFormValues(prev => ({ ...prev, paymentMethodsIds }));

    if (!!paymentMethodsIds.length) {
      setFormErrors(prev => deleteItemsFromObj(prev, FIELDS_IDS.PAYMENT_METHODS));
    } else {
      setFormErrors(prev => ({ ...prev, [FIELDS_IDS.PAYMENT_METHODS]: ' ' }));
    }
  }, [savedMethods]);

  const managePaymentMethodsHandler = () => {
    amplitude.track(BULK_PAYMENT_LINKS_EVENTS.PAYMENT_METHODS_MODAL_OPEN, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive)
    });

    setIsModalOpen(true);
  };

  const fixedAmountChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const amount = e.target.value;
    setFixedAmount(e.target.value);

    const error = fixedAmountValidator(amount, currentCurrency as string);
    const toCentsFactor = isThousandOperand(currentCurrency as string) ? 1000 : 100;

    setFormValues(prev => ({
      ...prev,
      ...(
        amountsFrom === AMOUNTS_FROM.FIXED ?
          {
            amountCents: amount ?
              (parseFloat(amount) * toCentsFactor).toFixed(0)
              :
              undefined
          }
          :
          {}
      )
    }));

    if (error) {
      setFormErrors(prev => ({ ...prev, [FIELDS_IDS.FIXED_AMOUNT]: error }));
    } else {
      setFormErrors(prev => deleteItemsFromObj(prev, FIELDS_IDS.FIXED_AMOUNT));
    }
  };

  const onFocusHandler = (e: FocusEvent<HTMLInputElement>) => {
    fixedAmountValueRef.current = e.target.value;
  };

  const onBlurHandler = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;

    value && value !== fixedAmountValueRef.current &&
      amplitude.track(BULK_PAYMENT_LINKS_EVENTS.FIXED_AMOUNT_SET, {
        [BULK_PAYMENT_LINKS_EVENTS_PARAMS.AMOUNT_VALUE]: value,
        [SHARED_EVENTS_PARAMS.CURRENCY]: currentCurrency
      });
  };

  const amountFromOptions = [
    {
      text: t('innerLayout.paymentLinksCreationPages.sections.payment.uploadedSheet'),
      amountsFrom: AMOUNTS_FROM.UPLOADED
    },
    {
      text: t('innerLayout.paymentLinksCreationPages.sections.payment.fixedAmount'),
      amountsFrom: AMOUNTS_FROM.FIXED
    }
  ];

  return (
    fullPageStatus.loading ?
      <SectionLoader {...BULK_PLS_SECTIONS_DIMENSIONS.PAYMENT} />
      :
      <div className={styles["payment-section"]}>

        <div className={styles["payment-section__title"]}>
          {t('innerLayout.paymentLinksCreationPages.sections.payment.title')}
        </div>

        <div className={styles["payment-section__amounts-box"]}>
          <ItemSelectorGroup
            label={t('innerLayout.paymentLinksCreationPages.sections.payment.UseAmountsFrom')}
            type="radio"
            items={
              amountFromOptions.map((option, i) => ({
                id: `uploaded-from-option-${i}`,
                text: option.text,
                value: option.amountsFrom,
                checked: option.amountsFrom === amountsFrom,
                onClickHandler: () => {
                  setAmountsFrom(option.amountsFrom);
                  setFormValues(prev => ({ ...prev, useFixedAmount: option.amountsFrom === AMOUNTS_FROM.FIXED }))
                }
              }))
            }
          />
        </div>
        {
          amountsFrom === AMOUNTS_FROM.FIXED &&
          <div className={styles["payment-section__fixed-amount-input"]}>
            <Input
              name="bulk-pl-fixed-amount-input"
              label={
                <>
                  {t('innerLayout.paymentLinksCreationPages.labels.amount')}
                  {<span className={styles["required-sign"]}> *</span>}
                </> as any
              }
              required={true}
              type="number"
              placeholder={t('innerLayout.paymentLinksCreationPages.placeholders.fixedAmountPlaceholder')}
              leadingText={getFormattedCurrency(currentCurrency as string)}
              value={fixedAmount}
              status={formErrors?.[FIELDS_IDS.FIXED_AMOUNT] ? 'error' : 'default'}
              error={formErrors?.[FIELDS_IDS.FIXED_AMOUNT]}
              onChange={fixedAmountChangeHandler}
              onKeyDown={preventExponentialNotationInput}
              onFocus={onFocusHandler}
              onBlur={onBlurHandler}
            />
          </div>
        }

        <div className={styles["payment-section__methods-box"]}>

          <div className={styles["payment-section__methods-box__badges-box"]}>
            {
              !isPreSelectedMethodsLoading && !isPreSelectedMethodsError &&
              <RemovableBadgesGroup
                list={savedMethods || []}
                badgesColor="grey"
                maxVisibleBadges={3}
                onChange={(updatedList) => {
                  setSavedMethods(updatedList as TSelectedMethod);
                  setTempSelectedMethods(updatedList as TSelectedMethod);
                }}
                badgeTextFormatter={badgeTextFormatter}
                remainingItemsBadgeTextFormatter={remainingItemsBadgeTextFormatter}
                emptyListText={t('innerLayout.paymentLinksCreationPages.errors.selectAtLeast1Method')}
              />
            }
          </div>
          <div className={styles['payment-section__methods-box__actions']}>
            <Button
              size="md"
              variant="link"
              text={t('innerLayout.paymentLinksCreationPages.sections.payment.managePaymentMethods')}
              onClickHandler={managePaymentMethodsHandler}
              disabled={isMethodsLoading || !canViewPaymentIntegrations}
            />
            {!canViewPaymentIntegrations && <Tooltip
              text={t(
                'innerLayout.paymentLinksCreationPages.errors.noPaymentIntegrationsPermission',
              )}
              triggerOn="hover"
              centerArrow
            >
              <div className={styles["payment-section__methods-box__actions__tooltip-icon"]}>
                {CircleInfo}
              </div>
            </Tooltip>}
          </div>

          <Checkbox
            type="checkbox"
            id="bulk-ql-save-methods"
            text={t('innerLayout.paymentLinksCreationPages.sections.payment.saveMethods')}
            onClickHandler={(checked) => setFormValues(prev => ({ ...prev, saveSelection: checked }))}
          />

        </div>

        <Modal
          position="center"
          open={isModalOpen}
          title={t('innerLayout.paymentLinksCreationPages.modals.methodsforLink')}
          btnsProps={modalActionBtns}
          closeOnClickOutside={false}
        >

          <div className={styles["payment-section__methods-modal"]}>
            {
              <PaymentMethodsTogglesGroup
                loading={isMethodsLoading}
                currenciesDropdownProps={{
                  disabled: !canChangeCurrency,
                  label: t('innerLayout.paymentLinksCreationPages.labels.currency'),
                  defaultCurrency: currentCurrency,
                  currenciesList: availableCurrencies,
                  onClickHandler: (curr) => {
                    setCurrentCurrency(curr);
                    setTempSelectedMethods(undefined);
                    setSavedMethods(undefined);
                    queryClient.setQueryData([QUERY_KEYS.GET_MERCHANT_PRESELECTED_METHODS, isLive], {});
                  },
                  formatCurrency: getFormattedCurrency
                }}
                paymentMethodsList={orderedPaymentMethods}
                integrationsDropdownProps={{
                  dropdown: {
                    width: "full",
                    label: t('innerLayout.paymentLinksCreationPages.modals.integrationId')
                  },
                  dropdownBtn: {
                    type: "input",
                    text: (gatewayName) => {
                      const selectedGateway = tempSelectedMethods?.[gatewayName];
                      return (
                        selectedGateway?.integrationName ||
                        (selectedGateway?.id ? String(selectedGateway?.id) : '')
                      );
                    },
                    placeholder: t('innerLayout.paymentLinksCreationPages.sections.payment.selectIntegration')
                  },
                  dropdownMenu: {
                    verticalPlacement: "auto"
                  }
                }}
                toggleBoxProps={{
                  type: "transparent box",
                  onToggleChangeHandler: (checked, gatewayName, integrations: any, hasOneIntegration) => {
                    if (checked) {
                      setTempSelectedMethods(prev => ({
                        ...prev,
                        [gatewayName]: {
                          gatewayName: gatewayName,
                          ...(
                            hasOneIntegration ?
                              {
                                id: integrations[0].id,
                                integrationName: integrations[0].integration_name,
                              }
                              :
                              {
                                id: prev?.[gatewayName]?.id || null,
                                integrationName: prev?.[gatewayName]?.integrationName || null,
                              }
                          )
                        }
                      }))
                    } else {
                      setTempSelectedMethods(prev => {
                        const { [gatewayName]: removed, ...rest } = prev || {};
                        return rest;
                      })
                    }
                  },
                  getToggleText: (gatewayName, integrations, hasOneIntegration) => {
                    return hasOneIntegration ?
                      `${getPaymentMethod(gatewayName).name} / ${integrations[0].integration_name || integrations[0].id}`
                      :
                      getPaymentMethod(gatewayName).name;
                  },
                  getToggleIcon: (gatewayName) => getPaymentMethod(gatewayName).icon as ReactElement<'svg'>
                }}
                tempSelectedMethods={tempSelectedMethods}
                setTempSelectedMethods={setTempSelectedMethods}
              />
            }
          </div>

        </Modal>
      </div>
  )
}
